import React, { useState } from 'react'
import { Dispatch } from 'redux'
import LoaderButton from '../../../components/loaderButton'
import { ModalWrapper } from '../../../components/modalWrapper'
import { displayNotification } from '../../../actions/notificationsActions'
import { request } from '../../../helpers/apiHelper'
import { dateTimeFormat } from '../../../helpers/timeHelper'
import { ProductPackage, User } from '../../../core/types'
import { fetchUser } from '../userActions'
import { backOutProductPackageMutation } from '../queries'

// Shown only to users holding this badge. The server enforces it (and the
// selling-organization scope) again on every request; this is just the UI half.
export const REFUND_BADGE = 'REFUND_DOWN_PAYMENT'

type Mode = 'refund_and_back_out' | 'record_existing_refund'

const MODE_ENUM: Record<Mode, string> = {
  refund_and_back_out: 'REFUND_AND_BACK_OUT',
  record_existing_refund: 'RECORD_EXISTING_REFUND',
}

const REASON_MAX = 500

interface Props {
  productPackage: ProductPackage
  user: User
  dispatch: Dispatch<any>
  currentUserBadges?: string[]
}

export const formatCents = (cents: number | null | undefined): string =>
  cents == null ? '-' : `$${(cents / 100).toFixed(2)}`

const BackOutDownPaymentBtn: React.FC<Props> = ({
  productPackage,
  user,
  dispatch,
  currentUserBadges = [],
}) => {
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<Mode>('refund_and_back_out')
  const [reason, setReason] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const eligibility = productPackage?.backOutEligibility
  const refund = productPackage?.downPaymentRefund ?? null

  // Nothing renders without the badge or without the server's verdict, so a
  // package fetched by a query that does not ask for them shows no new UI.
  if (!currentUserBadges.includes(REFUND_BADGE) || !eligibility) return null

  if (refund?.state === 'completed') {
    return (
      <li className="user-vehicles__line" data-testid="back-out-done">
        {refund.mode === 'record_existing_refund'
          ? 'Back-out recorded'
          : 'Down payment refunded and deal backed out'}
        {refund.completedAt ? ` on ${dateTimeFormat(refund.completedAt)}` : ''}
        {refund.actorName ? ` by ${refund.actorName}` : ''}
        {` - ${formatCents(refund.amountCents)}`}
      </li>
    )
  }

  if (!eligibility.eligible) {
    // Only the case a user could otherwise wonder about is spelled out: the deal
    // looks refundable but was paid through EFS. Every other reason (wrong
    // status, remitted, ...) is the normal state of most packages.
    if (eligibility.reasonCode === 'EFS_MANAGED_DOWN_PAYMENT') {
      return (
        <li className="user-vehicles__line" data-testid="back-out-blocked">
          {eligibility.message ||
            'This down payment was processed through EFS and cannot be refunded here.'}
        </li>
      )
    }
    return null
  }

  // A previous attempt that moved money (or may have) but did not finish. The
  // server resumes it and re-checks FluidPay first, so it can never double refund.
  const unfinished =
    refund && (refund.state === 'pending' || refund.state === 'provider_succeeded')
      ? refund
      : null
  const lastFailure = refund && refund.state === 'failed' ? refund : null

  const effectiveMode: Mode = unfinished ? unfinished.mode : mode
  const isRecord = effectiveMode === 'record_existing_refund'
  const canSubmit = !submitting && confirmed && reason.trim().length > 0
  const defaultSubmitLabel = unfinished ? 'Resume' : 'Confirm'
  const submitLabel = error ? 'Try again' : defaultSubmitLabel

  const close = () => {
    if (submitting) return
    setOpen(false)
    setError(null)
    setConfirmed(false)
  }

  const submit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    try {
      const response = await request(backOutProductPackageMutation, {
        BackOutProductPackageInput: {
          id: productPackage.id,
          mode: MODE_ENUM[effectiveMode],
          reason: reason.trim(),
        },
      })
      if (response.data.errors) {
        throw new Error(response.data.errors[0].message)
      }

      const result = response.data.data.backOutProductPackage
      if (result.success) {
        dispatch(
          displayNotification(
            isRecord
              ? 'Back-out recorded. The deal is now unwound.'
              : 'Down payment refunded and deal backed out.',
            'success'
          )
        )
        setOpen(false)
        setReason('')
        setConfirmed(false)
        dispatch(fetchUser(user.id))
      } else {
        const message = result.message || 'The refund could not be completed.'
        setError(message)
        dispatch(displayNotification(message, 'error'))
        // An unfinished or busy attempt changes what the card should offer next.
        if (result.outcome === 'needs_retry' || result.outcome === 'busy') {
          dispatch(fetchUser(user.id))
        }
      }
    } catch (e) {
      const message = `Could not complete the refund - ${
        e instanceof Error ? e.message : String(e)
      }`
      setError(message)
      dispatch(displayNotification(message, 'error'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {unfinished && (
        <li className="user-vehicles__line status-pill--yellow" data-testid="back-out-unfinished">
          A refund attempt is unfinished
          {unfinished.errorMessage ? `: ${unfinished.errorMessage}` : '.'} Retrying checks FluidPay first, so the
          customer cannot be refunded twice.
        </li>
      )}
      {lastFailure && (
        <li className="user-vehicles__line" data-testid="back-out-last-failure">
          Last refund attempt failed: {lastFailure.errorMessage || 'unknown error'}
        </li>
      )}
      <LoaderButton
        type="button"
        className="button button-drive-america continue-application-drive-america"
        data-testid="back-out-open"
        onClick={() => setOpen(true)}
      >
        {unfinished ? 'Resume Refund & Back Out' : 'Refund & Back Out'}
      </LoaderButton>

      {open && (
        <ModalWrapper
          title={unfinished ? 'Resume refund & back out' : 'Refund & back out'}
          titleClass="h1"
          hideModal={close}
          width="620px"
          height="auto"
          xclose={true}
        >
          <div data-testid="back-out-modal">
            <p>
              Deal ID {productPackage.id}
              {user?.firstName || user?.lastName
                ? ` - ${[user.firstName, user.lastName].filter(Boolean).join(' ')}`
                : ''}
            </p>

            {!unfinished && (
              <fieldset disabled={submitting} style={{ border: 0, padding: 0, margin: '0 0 12px' }}>
                <label style={{ display: 'block', marginBottom: 6 }}>
                  <input
                    type="radio"
                    name="back-out-mode"
                    data-testid="back-out-mode-refund"
                    checked={mode === 'refund_and_back_out'}
                    onChange={() => {
                      setMode('refund_and_back_out')
                      setConfirmed(false)
                    }}
                  />{' '}
                  Refund the customer through FluidPay, then back out the deal
                </label>
                <label style={{ display: 'block' }}>
                  <input
                    type="radio"
                    name="back-out-mode"
                    data-testid="back-out-mode-record"
                    checked={mode === 'record_existing_refund'}
                    onChange={() => {
                      setMode('record_existing_refund')
                      setConfirmed(false)
                    }}
                  />{' '}
                  Already refunded or voided in FluidPay - verify it and back out the deal
                </label>
              </fieldset>
            )}

            <p data-testid="back-out-amount">
              {isRecord
                ? 'No money moves. FluidPay is checked and the deal is backed out only if the payment is already refunded or voided.'
                : `FluidPay will refund the full amount charged: ${formatCents(
                    eligibility.amountCents
                  )}. A payment that has not settled yet is voided instead.`}
            </p>

            <label htmlFor="back-out-reason" style={{ display: 'block' }}>
              Reason (required)
            </label>
            <textarea
              id="back-out-reason"
              data-testid="back-out-reason"
              value={reason}
              maxLength={REASON_MAX}
              rows={3}
              disabled={submitting}
              onChange={(e) => setReason(e.target.value)}
              style={{ width: '100%' }}
            />
            <div
              data-testid="back-out-reason-count"
              style={{ fontSize: 12, marginBottom: 8 }}
            >
              {/* what the server counts: the reason is trimmed before it is sent */}
              {reason.trim().length}/{REASON_MAX}
            </div>

            <label style={{ display: 'block', marginBottom: 12 }}>
              <input
                type="checkbox"
                data-testid="back-out-confirm"
                checked={confirmed}
                disabled={submitting}
                onChange={(e) => setConfirmed(e.target.checked)}
              />{' '}
              {isRecord
                ? 'I confirm this payment was already refunded or voided in FluidPay.'
                : 'I understand the customer will be refunded and this cannot be undone.'}
            </label>

            {error && (
              <p role="alert" data-testid="back-out-error" style={{ color: '#b00020' }}>
                {error}
              </p>
            )}

            <button type="button" onClick={close} disabled={submitting} data-testid="back-out-cancel">
              Cancel
            </button>{' '}
            <LoaderButton
              type="button"
              className="button button-drive-america"
              data-testid="back-out-submit"
              disabled={!canSubmit}
              isLoading={submitting}
              onClick={submit}
            >
              {submitLabel}
            </LoaderButton>
          </div>
        </ModalWrapper>
      )}
    </>
  )
}

export default BackOutDownPaymentBtn
